import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, Plus, RefreshCw, Clock, MapPin, Volume2, Target } from "lucide-react";
import { PODRUCJA, MJESTA, badgeZaPodrucje } from "@/lib/constants";
import { toast } from "sonner";

const sel = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-500";

export default function AiAssistant({ open, onClose, onAdd, api }) {
  const [form, setForm] = useState({ razred: "", podrucje: "", mjesto: "", trajanje: "", napomena: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.post(`${api}/ai/generate`, {
        razred: form.razred || null,
        podrucje: form.podrucje || null,
        mjesto: form.mjesto || null,
        trajanje: form.trajanje ? parseInt(form.trajanje) : null,
        napomena: form.napomena || null,
      });
      setResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška pri generiranju ideje.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto rounded-2xl" data-testid="ai-modal">
        <DialogHeader className="text-left">
          <DialogTitle className="font-display text-2xl font-black text-slate-900 flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-purple-500" /> AI asistent za ideje
          </DialogTitle>
          <DialogDescription>
            Recite što vam treba, a AI će osmisliti novu aktivnost spremnu za provedbu.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.05em] text-slate-400">Razred</label>
                <select data-testid="ai-razred" className={sel} value={form.razred}
                  onChange={(e) => setForm((f) => ({ ...f, razred: e.target.value }))}>
                  <option value="">Bilo koji</option>
                  {["1.", "2.", "3.", "4.", "kombinirani 1.-4."].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.05em] text-slate-400">Područje</label>
                <select data-testid="ai-podrucje" className={sel} value={form.podrucje}
                  onChange={(e) => setForm((f) => ({ ...f, podrucje: e.target.value }))}>
                  <option value="">Bilo koje</option>
                  {PODRUCJA.map((p) => <option key={p.value} value={p.value}>{p.value}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.05em] text-slate-400">Mjesto</label>
                <select data-testid="ai-mjesto" className={sel} value={form.mjesto}
                  onChange={(e) => setForm((f) => ({ ...f, mjesto: e.target.value }))}>
                  <option value="">Bilo gdje</option>
                  {MJESTA.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.05em] text-slate-400">Trajanje</label>
                <select data-testid="ai-trajanje" className={sel} value={form.trajanje}
                  onChange={(e) => setForm((f) => ({ ...f, trajanje: e.target.value }))}>
                  <option value="">Svejedno</option>
                  {[15, 30, 45, 60].map((t) => <option key={t} value={t}>oko {t} min</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.05em] text-slate-400">Vaše želje (opcionalno)</label>
              <textarea
                data-testid="ai-napomena"
                value={form.napomena}
                onChange={(e) => setForm((f) => ({ ...f, napomena: e.target.value }))}
                placeholder="npr. igra za kišni dan, bez rekvizita, djeca su danas jako nemirna…"
                rows={3}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              />
            </div>
            <Button data-testid="ai-generate-btn" onClick={generate} disabled={loading}
              className="w-full rounded-full bg-purple-600 hover:bg-purple-700 py-6 text-base font-bold">
              {loading ? (<><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Osmišljavam ideju…</>) : (<><Sparkles className="h-5 w-5 mr-2" /> Generiraj ideju</>)}
            </Button>
          </div>
        )}

        {result && (
          <div className="space-y-4" data-testid="ai-result">
            <div className="rounded-2xl border border-purple-200 bg-purple-50/50 p-5">
              <span className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-[0.05em] ${badgeZaPodrucje(result.podrucje)}`}>
                {result.podrucje}
              </span>
              <h3 className="font-display text-xl font-black text-slate-900 mt-2">{result.naziv}</h3>
              <p className="text-sm text-slate-600 mt-1">{result.opis}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 text-sm font-medium text-slate-600">
                <span className="flex items-center gap-1"><Clock className="h-4 w-4 text-purple-500" />{result.trajanje} min</span>
                <span className="flex items-center gap-1"><MapPin className="h-4 w-4 text-purple-500" />{result.mjesto}</span>
                <span className="flex items-center gap-1"><Volume2 className="h-4 w-4 text-purple-500" />{result.razina_buke}</span>
              </div>
              {result.materijali?.length > 0 && (
                <p className="text-sm text-slate-600 mt-2"><span className="font-bold">Materijali:</span> {result.materijali.join(", ")}</p>
              )}
              <ol className="mt-3 space-y-2">
                {result.koraci?.map((k, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-slate-700">
                    <span className="h-5 w-5 shrink-0 rounded-full bg-purple-600 text-white text-[10px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                    {k}
                  </li>
                ))}
              </ol>
              <p className="mt-3 text-sm text-purple-900 flex gap-1.5"><Target className="h-4 w-4 shrink-0 mt-0.5" /><span><span className="font-bold">Cilj:</span> {result.cilj}</span></p>
            </div>
            <div className="flex gap-2">
              <Button data-testid="ai-add-to-plan" onClick={() => { onAdd(result); setResult(null); onClose(); }}
                className="flex-1 rounded-full bg-indigo-600 hover:bg-indigo-700 font-bold">
                <Plus className="h-4 w-4 mr-1.5" /> Dodaj u plan
              </Button>
              <Button data-testid="ai-retry-btn" onClick={generate} disabled={loading} variant="outline" className="rounded-full">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
